import React, { useEffect, useState } from 'react'
import InlineIcon from '../InlineIcon/InlineIcon.jsx'

import './EnvironmentBanner.scss'

const BANNER_HEIGHT = 41 // Height of the banner in pixels

const getEnvironmentInfo = () => {
  const hostname = window.location.hostname
  // Must be a bare, static `process.env.X` read — Vite `define` replaces it
  // with a literal at build time. Guards like `typeof process` or
  // `process.env?.X` defeat the replacement and the value comes back undefined
  // in the browser. Every key read this way must be listed in ENV_KEYS in
  // vite.config.mjs, or the deployed bundle throws ReferenceError.
  const backendEnv = process.env.BACKEND_ENV

  // Deployed to staging - detect from hostname
  if (hostname.includes('staging')) {
    return {
      show: true,
      message: (
        <>
          You are currently on <strong>STAGING</strong> env
        </>
      ),
      isProduction: false,
    }
  }

  // Deployed to development - detect from hostname
  if (hostname.includes('dev')) {
    return {
      show: true,
      message: (
        <>
          You are currently on <strong>DEVELOPMENT</strong> env
        </>
      ),
      isProduction: false,
    }
  }

  // Deployed to sales - detect from hostname
  if (hostname.includes('sales') || hostname.includes('demo')) {
    return {
      show: true,
      message: (
        <>
          You are currently on <strong>DEMO</strong> env
        </>
      ),
      isProduction: false,
    }
  }

  // Local FE with remote backend - use BACKEND_ENV
  if (hostname === 'localhost' || hostname.includes('lvh.me')) {
    if (backendEnv && ['dev', 'staging', 'production'].includes(backendEnv)) {
      const isProduction = backendEnv === 'production'
      return {
        show: true,
        message: (
          <>
            Connected to <strong>{backendEnv.toUpperCase()}</strong> backend
          </>
        ),
        isProduction,
      }
    }
    // Local backend (default)
    return {
      show: true,
      message: (
        <>
          Connected to <strong>LOCAL</strong> backend
        </>
      ),
      isProduction: false,
    }
  }

  // Production or unknown - no banner
  return { show: false }
}

const EnvironmentBanner = () => {
  const [dismissed, setDismissed] = useState(false)
  const envInfo = getEnvironmentInfo()

  // Set CSS variable on body to offset navbar and content
  useEffect(() => {
    if (envInfo.show && !dismissed) {
      document.body.style.setProperty(
        '--environment-banner-height',
        `${BANNER_HEIGHT}px`,
      )
    } else {
      document.body.style.setProperty('--environment-banner-height', '0px')
    }

    return () => {
      document.body.style.setProperty('--environment-banner-height', '0px')
    }
  }, [envInfo.show, dismissed])

  if (!envInfo.show || dismissed) {
    return null
  }

  return (
    <div
      className={`environment-banner ${
        envInfo.isProduction
          ? 'environment-banner--production'
          : 'environment-banner--info'
      }`}
    >
      <InlineIcon
        name={envInfo.isProduction ? 'alert-triangle' : 'info-circle'}
        className='environment-banner__icon'
      />
      <div className='environment-banner__content'>{envInfo.message}</div>
      <button
        type='button'
        className='environment-banner__close'
        aria-label='Dismiss environment banner'
        onClick={() => setDismissed(true)}
      >
        <InlineIcon name='close' />
      </button>
    </div>
  )
}

export default EnvironmentBanner
